import { useEffect, useState } from 'react';
import { Spinner } from 'reactstrap';
import { AppButton, AppSelectBox, AppSwitchBox } from 'components';
import AppBreadcrumbs from 'components/molecules/AppBreadcrumbs';
import { AppModal, AppModalHeader, AppModalBody } from 'components/molecules/Modal/AppModal';
import useESTList from 'hooks/API/GET/useESTList';
import useCourses from 'hooks/API/GET/useCourses';
import useChapters from 'hooks/API/GET/useChapters';
import useHtmlConvert, { useEddyIndex, conversionError } from 'hooks/API/useHtmlConvert';

// What differs between the two jobs on this page: the words, and which counts the report has.
const HTML = {
  title: 'Convert to HTML',
  intro: [
    'Notes are normally converted to HTML automatically when they are saved. Use this to convert existing content again in bulk, for example after a fix in the converter or after a bulk upload.',
    'Learners read the HTML version of each lesson, sub-lesson and Geek Corner. You will be asked to confirm and to choose what to include. When it finishes, download the PDF report: it lists every item and the reason for any failure.',
  ],
  checking: 'Checking conversion availability...',
  switchedOff: 'HTML conversion is currently switched off.',
  adminsOnly: 'HTML conversion is available to full administrators only.',
  notConfigured: 'HTML conversion is not configured for this tenant yet.',
  chapterButton: 'Convert chapter',
  courseButton: 'Convert entire course',
  startButton: 'Start conversion',
  confirmTitle: 'Confirm HTML conversion',
  confirmNote:
    'This updates the stored HTML of the content you chose. A large course can take several minutes. A note that fails keeps its old HTML.',
  runningTitle: 'Conversion in progress',
  doneTitle: 'Conversion completed',
  failedTitle: 'Conversion failed',
  working: 'Regenerating HTML. This can take several minutes.',
  failedText: 'Some content could not be converted.',
  busyText: 'A conversion is already running',
  starting: 'Starting conversion...',
  counts: [
    ['converted', 'Converted'],
    ['alreadyHasHtml', 'Already had HTML'],
    ['noContent', 'No content'],
    ['noBlocks', 'Skipped (no blocks)'],
    ['filteredStatus', 'Not converted because of status'],
    ['failed', 'Failed'],
  ],
};

const EDDY = {
  title: 'Ask Eddy indexing',
  intro: [
    'Ask Eddy is the learners’ AI tutor, and it can only answer from content that has been indexed. Notes are indexed automatically when they are saved. Use this for older content, or after a bulk upload or a course copy.',
    'Tip: run Convert to HTML first and index afterwards, so Eddy reads the final version of the notes. Content that has not changed is skipped, so it is safe to run again. Download the PDF report when it finishes.',
  ],
  checking: 'Checking Ask Eddy availability...',
  switchedOff: 'Ask Eddy indexing is currently switched off.',
  adminsOnly: 'Ask Eddy indexing is available to full administrators only.',
  notConfigured: 'Ask Eddy indexing is not configured for this tenant yet.',
  chapterButton: 'Index chapter',
  courseButton: 'Index entire course',
  startButton: 'Start indexing',
  confirmTitle: 'Confirm Ask Eddy indexing',
  confirmNote:
    'Every note and Geek Corner item in your choice is sent to Ask Eddy. Items that have not changed are skipped. A whole course can take a long time, and you can leave this page while it runs.',
  runningTitle: 'Indexing in progress',
  doneTitle: 'Indexing completed',
  failedTitle: 'Indexing failed',
  working: 'Ask Eddy is reading the content. This can take a while.',
  failedText: 'Some content could not be indexed.',
  busyText: 'An Ask Eddy indexing run is already running',
  starting: 'Starting indexing...',
  counts: [
    ['indexed', 'Indexed'],
    ['stillQueued', 'Still queued'],
    ['noText', 'No text to index'],
    ['failed', 'Failed'],
  ],
};

function Options({ query, labelKey }) {
  if (query.isLoading) return <option value="">Loading...</option>;
  if (query.isError) return <option value="">Failed to load list</option>;
  const items = query.data?.data || [];
  return (
    <>
      <option value="">{items.length ? 'Choose' : 'No options found'}</option>
      {items.map((item) => (
        <option key={item._id} value={item._id}>
          {item[labelKey]}
        </option>
      ))}
    </>
  );
}

function JobResult({ job, download, copy }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (job.state !== 'running') return;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [job.state]);
  const seconds =
    Math.max(
      0,
      Math.floor(
        ((job.finishedAt ? Date.parse(job.finishedAt) : now) - Date.parse(job.startedAt)) / 1000
      )
    ) || 0;
  return (
    <section className="border rounded p-4 mt-5" aria-live="polite">
      <h4 className="prime-head p-0 boldtitle mb-3">
        {job.state === 'running'
          ? copy.runningTitle
          : job.state === 'done'
          ? copy.doneTitle
          : copy.failedTitle}
      </h4>
      <p>
        Started by {job.requestedBy || 'an admin'} on {new Date(job.startedAt).toLocaleString()}
      </p>
      <p>
        Elapsed time: {Math.floor(seconds / 60)}m {seconds % 60}s
      </p>
      {job.state === 'running' && (
        <>
          <Spinner size="sm" tag="span" /> {copy.working}
        </>
      )}
      {job.counts && (
        <dl className="row mb-0 mt-4">
          {copy.counts.map(([key, label]) => (
            <div className="col-lg-4 col-md-6 col-sm-12 mb-3" key={key}>
              <dt>{label}</dt>
              <dd>{job.counts[key] ?? 0}</dd>
            </div>
          ))}
        </dl>
      )}
      {job.state === 'failed' && (
        <p role="alert" className="text-danger">
          {job.error || copy.failedText}
        </p>
      )}
      {job.state !== 'running' && job.report && (
        <AppButton
          variantClass="btn-primary"
          disabled={download.isLoading}
          onClick={() => download.mutate()}
        >
          {download.isLoading ? 'Downloading...' : 'Download report (PDF)'}
        </AppButton>
      )}
      {download.isError && (
        <p role="alert" className="text-danger">
          {conversionError(download.error)}
        </p>
      )}
    </section>
  );
}

/**
 * One job on the page (HTML conversion or Ask Eddy indexing): its availability message, its two buttons,
 * notices and the result of the current job. The content to act on is chosen once, above both.
 */
function JobSection({ copy, job: hook, ready, state, chapter, course, busy, onAsk }) {
  const { status, job, jobQuery, start, conflict, download } = hook;
  return (
    <div className="mt-5">
      <div className="row">
        <div className="col-12">
          <h3 className="prime-head p-0 boldtitle">{copy.title}</h3>
        </div>
      </div>
      <div className="mt-3">
        {status.isLoading ? (
          <p>
            <Spinner size="sm" tag="span" /> {copy.checking}
          </p>
        ) : status.isError ? (
          <div role="alert">
            <p>{conversionError(status.error)}</p>
            <AppButton variantClass="btn-primary" onClick={() => status.refetch()}>
              Retry
            </AppButton>
          </div>
        ) : !state.allowed ? (
          <p>{state.permitted ? copy.switchedOff : copy.adminsOnly}</p>
        ) : (
          <>
            {!ready && <p role="status">{copy.notConfigured}</p>}
            {[].concat(copy.intro).map((text) => (
              <p key={text}>{text}</p>
            ))}
            <div className="row mt-3">
              <div className="col-12 btnTab-foot d-flex justify-content-end">
                <AppButton
                  variantClass="btn-primary mx-3"
                  disabled={!chapter || busy || !ready}
                  onClick={() => onAsk('chapter')}
                >
                  {copy.chapterButton}
                </AppButton>
                <AppButton
                  variantClass="btn-primary mx-3"
                  disabled={!course || busy || !ready}
                  onClick={() => onAsk('course')}
                >
                  {copy.courseButton}
                </AppButton>
              </div>
            </div>
            {start.isLoading && (
              <p className="mt-3">
                <Spinner size="sm" tag="span" /> {copy.starting}
              </p>
            )}
            {(conflict || (status.data?.busy && !status.data?.running)) && (
              <p role="alert" className="mt-3">
                {copy.busyText}
                {conflict?.requestedBy ? `, started by ${conflict.requestedBy}` : ''}
                {conflict?.startedAt
                  ? ` since ${new Date(conflict.startedAt).toLocaleString()}`
                  : ''}
                .
              </p>
            )}
            {start.isError && start.error?.response?.status !== 409 && (
              <p role="alert" className="text-danger mt-3">
                {conversionError(start.error)}
              </p>
            )}
            {jobQuery.isError && (
              <p role="alert" className="text-danger">
                Progress could not be refreshed. Retrying every 3 seconds.
              </p>
            )}
            {job && <JobResult key={job.jobId} job={job} download={download} copy={copy} />}
          </>
        )}
      </div>
    </div>
  );
}

const isBusy = ({ start, job, status }) =>
  start.isLoading || job?.state === 'running' || (!!status.data?.busy && !status.data?.running);

export default function AdminCentre() {
  const html = useHtmlConvert();
  const eddy = useEddyIndex();
  const [region, setRegion] = useState('');
  const [course, setCourse] = useState('');
  const [chapter, setChapter] = useState('');
  const [confirmation, setConfirmation] = useState(null);
  const [force, setForce] = useState(false);
  const [allStatus, setAllStatus] = useState(false);

  const htmlState = {
    permitted: !!html.status.data?.canConvert,
    allowed: !!html.status.data?.canConvert && !!html.status.data?.enabled,
  };
  const eddyState = {
    permitted: !!eddy.status.data?.allowed,
    allowed: !!eddy.status.data?.allowed && !!eddy.status.data?.enabled,
  };
  const htmlReady =
    htmlState.allowed &&
    !!html.status.data?.renderEngineConfigured &&
    !!html.status.data?.databases?.length;
  const eddyReady =
    eddyState.allowed && !!eddy.status.data?.configured && !!eddy.status.data?.databases?.length;
  const anyReady = htmlReady || eddyReady;
  const htmlBusy = isBusy(html);
  const eddyBusy = isBusy(eddy);

  const regions = useESTList({ enabled: anyReady, queryParams: '?mode=dropdown' });
  const courses = useCourses({
    enabled: anyReady && !!region,
    configs: { params: { mode: 'dropdown', estId: region, allCourses: true } },
  });
  const chapters = useChapters({
    enabled: anyReady && !!course,
    classID: 'all',
    courseID: course,
    configs: { params: { mode: 'dropdown' } },
  });
  const courseName = courses.data?.data?.find((item) => item._id === course)?.courseName || '';
  const chapterName = chapters.data?.data?.find((item) => item._id === chapter)?.name || '';

  const copy = confirmation?.feature === 'eddy' ? EDDY : HTML;

  function confirm(feature, scope) {
    (feature === 'eddy' ? eddy : html).start.reset();
    setForce(false);
    setAllStatus(false);
    setConfirmation({ feature, scope, id: scope === 'chapter' ? chapter : course });
  }

  function submit() {
    if (!confirmation) return;
    const { feature, ...request } = confirmation;
    if (feature === 'eddy') {
      if (eddyBusy) return;
      eddy.start.mutate(request);
    } else {
      if (htmlBusy) return;
      html.start.mutate({ ...request, force, allStatus });
    }
    setConfirmation(null);
  }

  return (
    <div className="mainWraper coursetabsection">
      <div className="tab">
        <div className="tab__content ___adjust___tab___padding">
          <div className="tab__content-actHead">
            <AppBreadcrumbs
              links={[{ label: 'Dashboard', to: '/dashboard' }]}
              current="Admin Centre"
            />
          </div>
          <div className="tab__content-actBody">
            <div className="px-5 py-5">
              <div className="row">
                <div className="col-12">
                  <h2 className="prime-head p-0 boldtitle">Admin Centre</h2>
                </div>
              </div>
              {anyReady && (
                <fieldset className="mt-4" disabled={htmlBusy || eddyBusy}>
                  <p>
                    <strong>Step 1: choose the content.</strong> Pick a region and a course. Pick a
                    chapter to work on a single chapter, or leave the chapter empty and use the
                    &quot;entire course&quot; buttons. Both actions below use this choice.
                  </p>
                  <div className="row">
                    <div className="col-lg-4 col-md-6 col-sm-12 mb-3">
                      <AppSelectBox
                        labelProps={{ label: 'Region' }}
                        selectProps={{
                          value: region,
                          onChange: (event) => {
                            setRegion(event.target.value);
                            setCourse('');
                            setChapter('');
                          },
                        }}
                      >
                        <Options query={regions} labelKey="name" />
                      </AppSelectBox>
                    </div>
                    <div className="col-lg-4 col-md-6 col-sm-12 mb-3">
                      <AppSelectBox
                        labelProps={{ label: 'Course' }}
                        selectProps={{
                          value: course,
                          disabled: !region || courses.isLoading || courses.isError,
                          onChange: (event) => {
                            setCourse(event.target.value);
                            setChapter('');
                          },
                        }}
                      >
                        <Options query={courses} labelKey="courseName" />
                      </AppSelectBox>
                    </div>
                    <div className="col-lg-4 col-md-6 col-sm-12 mb-3">
                      <AppSelectBox
                        labelProps={{ label: 'Chapter (optional)' }}
                        selectProps={{
                          value: chapter,
                          disabled: !course || chapters.isLoading || chapters.isError,
                          onChange: (event) => setChapter(event.target.value),
                        }}
                      >
                        <Options query={chapters} labelKey="name" />
                      </AppSelectBox>
                    </div>
                  </div>
                </fieldset>
              )}
              <JobSection
                copy={HTML}
                job={html}
                ready={htmlReady}
                state={htmlState}
                chapter={chapter}
                course={course}
                busy={htmlBusy}
                onAsk={(scope) => confirm('html', scope)}
              />
              <JobSection
                copy={EDDY}
                job={eddy}
                ready={eddyReady}
                state={eddyState}
                chapter={chapter}
                course={course}
                busy={eddyBusy}
                onAsk={(scope) => confirm('eddy', scope)}
              />
            </div>
          </div>
        </div>
      </div>
      <AppModal
        open={!!confirmation}
        closeModal={() => setConfirmation(null)}
        size="md"
        modalProps={{ role: 'dialog', 'aria-label': copy.confirmTitle }}
      >
        <AppModalHeader>{copy.confirmTitle}</AppModalHeader>
        <AppModalBody>
          <p className="modal-sub-text text-center">
            {confirmation?.feature === 'eddy' ? 'Index' : 'Regenerate HTML for'}{' '}
            <strong>{courseName}</strong>
            {confirmation?.scope === 'chapter' && (
              <>
                {' '}
                / <strong>{chapterName}</strong>
              </>
            )}
            ?
          </p>
          <p className="modal-sub-text text-center">{copy.confirmNote}</p>
          {confirmation?.feature !== 'eddy' && (
            <>
              <p className="modal-sub-text text-center">
                <strong>What to include.</strong> Both options are off by default, which is the safe
                choice.
              </p>
              <div className="mb-3">
                <AppSwitchBox
                  labelProps={{ label: 'Re-convert notes that already have HTML' }}
                  inputProps={{
                    checked: force,
                    onChange: (event) => setForce(event.target.checked),
                  }}
                />
                <small className="text-muted d-block">
                  Off: notes that already have HTML are skipped. On: HTML is generated again and the
                  old HTML is replaced.
                </small>
              </div>
              <div className="mb-3">
                <AppSwitchBox
                  labelProps={{ label: 'Include draft and review notes' }}
                  inputProps={{
                    checked: allStatus,
                    onChange: (event) => setAllStatus(event.target.checked),
                  }}
                />
                <small className="text-muted d-block">
                  Off: only Active and Teacher-verified notes are converted. On: draft, review and
                  other notes are converted too.
                </small>
              </div>
            </>
          )}
          <div className="app-l-modal-btn-block">
            <AppButton variantClass="btn-plane p--tint mx-4" onClick={() => setConfirmation(null)}>
              Cancel
            </AppButton>
            <AppButton
              variantClass="btn-primary mx-4"
              disabled={
                confirmation?.feature === 'eddy' ? eddyBusy || !eddyReady : htmlBusy || !htmlReady
              }
              onClick={submit}
            >
              {copy.startButton}
            </AppButton>
          </div>
        </AppModalBody>
      </AppModal>
    </div>
  );
}
